import type { CaseRecordUpdateDescription } from '@compass/client/types/case_record';
import { cn } from '@utils/index';
import { ArrowRight } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import type { FormField } from '@/types/formSchema';
import {
  diffValues,
  flattenUpdatedFields,
  formatFieldPath,
  getFieldSchema,
  getNestedValue,
  groupBySource,
  HIDDEN_FIELD_PATHS,
  SOURCE_LABELS,
  type ValueChange,
} from '@/utils/caseHistoryDiff';
import { formatDateForDisplay } from '@/utils/date';
import { formatFieldName } from '@/utils/string';

interface CaseHistoryDiffProps {
  updateDescription: CaseRecordUpdateDescription | undefined;
  className?: string;
  /** Map of field IDs to their full schema definitions from the workflow UI schema */
  fieldSchemaMap?: Record<string, FormField>;
  /** Previous values for this entry's changed fields */
  previousValues?: Record<string, any>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isComplexValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(item => isPlainObject(item));
  }
  return isPlainObject(value);
}

/** Format a primitive value for display, resolving select labels and formatting dates. */
function formatPrimitive(value: unknown, field?: FormField): string {
  if (value === null || value === undefined || value === '') return '';

  if (field?.options && field.options.length > 0) {
    const option = field.options.find(opt => opt.value === value);
    if (option) return option.label;
  }

  if ((field?.type === 'date' || field?.type === 'dob') && value !== null && value !== undefined) {
    return formatDateForDisplay(String(value));
  }

  if (typeof value === 'string' && /^[A-Z][A-Z0-9_]+$/.test(value)) {
    return formatFieldName(value.toLowerCase());
  }

  return String(value);
}

/** Format a field value for display, resolving select labels and formatting dates. */
function formatValue(value: unknown, field?: FormField): string {
  if (value === null || value === undefined) return '';

  if (field?.type === 'multi_select' && Array.isArray(value) && field.options) {
    if (value.length === 0) return '';
    return value.map(v => field.options?.find(opt => opt.value === v)?.label || v).join(', ');
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    if (value.every(v => !isPlainObject(v))) {
      return value
        .map(v => formatPrimitive(v))
        .filter(Boolean)
        .join(', ');
    }
  }

  return formatPrimitive(value, field);
}

/** Humanize a path segment: `[0]` → "Item 1", snake_case → "Snake Case". */
function formatPathSegment(seg: string): string {
  const arrayMatch = seg.match(/^\[(\d+)\]$/);
  if (arrayMatch) return `Item ${Number(arrayMatch[1]) + 1}`;
  return formatFieldName(seg);
}

function formatChangePath(path: string[], fieldSchemaMap?: Record<string, FormField>): string {
  return path
    .map((seg, idx) => {
      if (idx === 0 && fieldSchemaMap && seg in fieldSchemaMap) {
        return fieldSchemaMap[seg].label;
      }
      return formatPathSegment(seg);
    })
    .join(' › ');
}

/** Render a nested value (object/array) as a readable tree. */
function ValueTree({ value, depth = 0 }: { value: unknown; depth?: number }): React.ReactElement {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground italic">empty</span>;
    return (
      <div className="space-y-1.5">
        {value.map((item, idx) => (
          <div
            key={idx}
            className={cn('border-l-2 border-border pl-2', isPlainObject(item) ? 'py-1' : 'py-0.5')}
          >
            {isPlainObject(item) ? (
              <ValueTree value={item} depth={depth + 1} />
            ) : (
              <span>{formatPrimitive(item)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(
      ([, v]) => v !== '' && v !== null && v !== undefined,
    );
    if (entries.length === 0) return <span className="text-muted-foreground italic">empty</span>;
    return (
      <div className="space-y-1">
        {entries.map(([key, v]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <span className="text-[11px] text-muted-foreground">{formatFieldName(key)}</span>
            {isComplexValue(v) ? (
              <div className="pl-2">
                <ValueTree value={v} depth={depth + 1} />
              </div>
            ) : (
              <span className="text-sm">{formatPrimitive(v) || '--'}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return <span>{formatPrimitive(value) || '--'}</span>;
}

/** Single row showing one structural change with breadcrumb path + before/after. */
function ChangeRow({
  change,
  fieldSchemaMap,
}: {
  change: ValueChange;
  fieldSchemaMap?: Record<string, FormField>;
}): React.ReactElement {
  const breadcrumb = formatChangePath(change.path, fieldSchemaMap);
  const beforeComplex = isComplexValue(change.before);
  const afterComplex = isComplexValue(change.after);
  const eitherComplex = beforeComplex || afterComplex;

  if (change.kind === 'added') {
    return (
      <div className="px-2 py-1 -mx-2">
        <div className="text-xs text-muted-foreground mb-0.5 flex items-center justify-between gap-2">
          <span>{breadcrumb}</span>
          <Badge
            variant="success"
            className="px-1.5 py-0 text-[10px] uppercase bg-emerald-500 text-white shrink-0"
          >
            Added
          </Badge>
        </div>
        {afterComplex ? (
          <div className="text-sm pl-2">
            <ValueTree value={change.after} />
          </div>
        ) : (
          <span className="text-sm font-medium">{formatPrimitive(change.after) || '--'}</span>
        )}
      </div>
    );
  }

  if (change.kind === 'removed') {
    return (
      <div className="px-2 py-1 -mx-2">
        <div className="text-xs text-muted-foreground mb-0.5">
          <span className="text-destructive">− </span>
          {breadcrumb}
        </div>
        {beforeComplex ? (
          <div className="text-sm pl-2 line-through text-muted-foreground opacity-70">
            <ValueTree value={change.before} />
          </div>
        ) : (
          <span className="text-sm line-through text-muted-foreground">
            {formatPrimitive(change.before) || '--'}
          </span>
        )}
      </div>
    );
  }

  // 'changed' — primitive in both
  return (
    <div className="px-2 py-1 -mx-2">
      <div className="text-xs text-muted-foreground mb-0.5">{breadcrumb}</div>
      {eitherComplex ? (
        <div className="text-sm space-y-1">
          <div className="line-through text-muted-foreground opacity-70">
            <ValueTree value={change.before} />
          </div>
          <div className="font-medium">
            <ValueTree value={change.after} />
          </div>
        </div>
      ) : (
        <div className="text-sm flex items-center gap-1.5 flex-wrap">
          <span className="line-through text-muted-foreground">
            {formatPrimitive(change.before) || '--'}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="font-medium">{formatPrimitive(change.after) || '--'}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Component to display field changes (diff) for a case record audit entry.
 * Shows updated fields with old -> new values and removed fields.
 */
export const CaseHistoryDiff: React.FC<CaseHistoryDiffProps> = React.memo(
  ({ updateDescription, className, fieldSchemaMap, previousValues }) => {
    if (!updateDescription) {
      return null;
    }

    const { updated_fields, removed_fields } = updateDescription;
    const hasUpdatedFields = updated_fields && Object.keys(updated_fields).length > 0;
    const hasRemovedFields = removed_fields && removed_fields.length > 0;

    if (!hasUpdatedFields && !hasRemovedFields) {
      return null;
    }

    // Flatten and group updated fields by source
    const flattenedFields = hasUpdatedFields
      ? flattenUpdatedFields(updated_fields, previousValues)
      : [];
    const groupedFields = groupBySource(flattenedFields);
    // Filter removed fields to hide system metadata
    const visibleRemovedFields = hasRemovedFields
      ? removed_fields.filter(path => !HIDDEN_FIELD_PATHS.has(path))
      : [];

    if (flattenedFields.length === 0 && visibleRemovedFields.length === 0) {
      return null;
    }

    return (
      <div className={cn('rounded-lg border border-border p-3 space-y-4', className)}>
        {/* Updated fields grouped by source */}
        {Array.from(groupedFields.entries()).map(([source, fields]) => (
          <div key={source} className="space-y-3">
            {/* Section header — only show when there are multiple sources or it's a data section */}
            {(source === 'input_data' || source === 'output_data') && (
              <p className="text-[11px] font-semibold text-foreground">{SOURCE_LABELS[source]}</p>
            )}
            {fields.map(({ fieldPath, value, previousValue }) => {
              const field = getFieldSchema(fieldPath, fieldSchemaMap);
              const valueIsComplex = isComplexValue(value) || isComplexValue(previousValue);

              if (valueIsComplex) {
                const leafKey = fieldPath.split('.').pop() || fieldPath;
                const hasPrev = previousValue !== undefined && previousValue !== null;
                const changes = hasPrev ? diffValues(previousValue, value, [leafKey]) : null;

                if (changes && changes.length > 0) {
                  return (
                    <div key={fieldPath} className="px-2 py-1.5 -mx-2 space-y-1">
                      {changes.map((change, idx) => (
                        <ChangeRow
                          key={`${change.path.join('.')}-${idx}`}
                          change={change}
                          fieldSchemaMap={fieldSchemaMap}
                        />
                      ))}
                    </div>
                  );
                }

                // Field is set for the first time — render as a single "added" entry
                // with the new value tree, prefixed by a + indicator.
                if (!hasPrev) {
                  return (
                    <div key={fieldPath} className="px-2 py-1.5 -mx-2">
                      <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between gap-2">
                        <span>{formatFieldPath(fieldPath, fieldSchemaMap)}</span>
                        <Badge
                          variant="success"
                          className="px-1.5 py-0 text-[10px] uppercase bg-emerald-500 text-white shrink-0"
                        >
                          Added
                        </Badge>
                      </div>
                      <div className="text-sm pl-2">
                        <ValueTree value={value} />
                      </div>
                    </div>
                  );
                }

                // Previous value exists but diff is empty — nothing actually changed; skip.
                return null;
              }

              const newDisplay = formatValue(value, field);
              const prevDisplay =
                previousValue !== undefined ? formatValue(previousValue, field) : undefined;
              const hasPrevious =
                prevDisplay !== undefined && prevDisplay !== '' && prevDisplay !== newDisplay;

              return (
                <div key={fieldPath} className="px-2 py-1.5 -mx-2">
                  <div className="text-xs text-muted-foreground mb-0.5">
                    {formatFieldPath(fieldPath, fieldSchemaMap)}
                  </div>
                  <div className="text-sm flex items-center gap-1.5 flex-wrap">
                    {hasPrevious && (
                      <>
                        <span className="line-through text-muted-foreground">{prevDisplay}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                      </>
                    )}
                    <span className="font-medium">{newDisplay || '--'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Removed fields */}
        {visibleRemovedFields.length > 0 && (
          <div className="space-y-3">
            {visibleRemovedFields.map(fieldPath => {
              const field = getFieldSchema(fieldPath, fieldSchemaMap);
              const oldValue = previousValues
                ? getNestedValue(previousValues, fieldPath)
                : undefined;
              const valueIsComplex = isComplexValue(oldValue);

              if (valueIsComplex) {
                return (
                  <div key={fieldPath} className="px-2 py-1.5 -mx-2">
                    <div className="text-xs text-muted-foreground mb-1">
                      {formatFieldPath(fieldPath, fieldSchemaMap)}{' '}
                      <span className="text-destructive">(removed)</span>
                    </div>
                    <div className="text-sm text-muted-foreground opacity-70 line-through">
                      <ValueTree value={oldValue} />
                    </div>
                  </div>
                );
              }

              const oldDisplay = oldValue !== undefined ? formatValue(oldValue, field) : undefined;

              return (
                <div key={fieldPath} className="px-2 py-1.5 -mx-2">
                  <div className="text-xs text-muted-foreground mb-0.5">
                    {formatFieldPath(fieldPath, fieldSchemaMap)}
                  </div>
                  <div className="text-sm flex items-center gap-1.5">
                    {oldDisplay && (
                      <span className="line-through text-muted-foreground">{oldDisplay}</span>
                    )}
                    <span className="text-destructive">(removed)</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

CaseHistoryDiff.displayName = 'CaseHistoryDiff';
